import { Component, OnInit, ViewChild, Input, ElementRef, OnDestroy } from '@angular/core';
import { SharedService } from 'src/app/shared/services/shared.service';
import { HttpClient } from '@angular/common/http';
import { ParserDoc, Parser } from 'src/app/models/parsers.model';
import { Conversion } from 'src/app/models/conversion.model';
import { environment } from 'src/environments/environment';
import { ConversionData } from 'src/app/models/conversion.model';
import { getTotalCreditCount, User } from 'src/app/models/users.model';
import { NbButtonComponent, NbStepperComponent, NbDialogService, NbToastrService } from '@nebular/theme';
import { FileHandlerService } from 'src/app/shared/services/file-handler.service';
import { NbStepComponent } from '@nebular/theme/components/stepper/step.component';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { AddCreditsComponent } from 'src/app/shared/ui/add-credits/add-credits.component';
import { DialogComponent } from 'src/app/shared/ui/dialog/dialog.component';
import { take, map, startWith } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { groupBy } from 'lodash';
import { mapCodeToCountryName } from '../../../models/country-code-mapper';
import { CountryCode } from 'src/app/models/country.enum';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
const firestore = firebase.firestore;

interface Group {
  name: string;
  countryCode: string;
  children: { id: string; display: string }[];
}

@Component({
  selector: 'app-convert-1',
  templateUrl: './convert-1.component.html',
  styleUrls: ['./convert-1.component.scss'],
})
export class Convert1Component implements OnInit, OnDestroy {
  @Input()
  conversionData: ConversionData;
  @Input()
  stepper: NbStepperComponent;
  @ViewChild('fileInputEl', { static: true })
  fileInputEl: ElementRef;

  fetchingBanks = true;
  user: User;
  docparserResults: Parser[];
  form: UntypedFormGroup;
  pageCountTotal: number;

  stepperControl = new UntypedFormControl(); // Controls whether we can go to next step or not

  fileData: { file: File; pageCount: number }[];
  fileCheckError: string = null;
  private userSub: Subscription;

  groups: Group[];
  filteredGroups$: Observable<Group[]>;

  processing = false;

  countryMismatchError: string = null;
  isAiParserEnabled = false;
  showAiParser = false;

  get totalCreditCount() {
    if (!this.user) {
      return null;
    }
    return getTotalCreditCount(this.user);
  }

  constructor(
    private ss: SharedService,
    private toastr: NbToastrService,
    private http: HttpClient,
    private fileHandler: FileHandlerService,
    private fb: UntypedFormBuilder,
    private dialogService: NbDialogService,
    private featureFlag: FeatureFlagService,
  ) {
    this.initForm();
  }

  async ngOnInit() {
    this.user = await this.ss.getUser();
    // Keep the balance live so the credit check stays correct after a conversion (or top up) elsewhere
    this.userSub = this.ss.user$.subscribe((user) => {
      this.user = user;
      this.form.controls.files.updateValueAndValidity();
    });
    this.stepperControl.setErrors({ disabled: true }); // Prevent clicking steps or next until we set control valid
    this.stepper.selected.stepControl = this.stepperControl;
    this.isAiParserEnabled = await this.featureFlag.isEnabled('aiParserEnabled');
    this.showAiParser = this.isAiParserEnabled;
    await this.getDocparserDocs();
    this.initAutocomplete();
    if (this.isAiParserEnabled) this.form.controls.parserId.setValue('aiparser');
  }

  ngOnDestroy() {
    this.userSub?.unsubscribe();
  }

  private initAutocomplete() {
    const groupedByCountry = groupBy(this.docparserResults, (a) => a.country);

    this.groups = Object.entries(groupedByCountry).map(([country, dpResults]) => {
      return {
        name: country,
        countryCode: dpResults?.[0]?.countryCode,
        children: dpResults.map((a) => ({ id: a.dpParserId, display: a.name })),
      };
    });

    this.groups.sort((a, b) => {
      // Move nulls to end
      if (!a.countryCode) {
        return 1;
      }
      if (!b.countryCode) {
        return -1;
      }
      // Move selected country to top
      if (a.countryCode.toLocaleLowerCase().startsWith(this.user.countryCode?.toLocaleLowerCase() || CountryCode.ZA.toLocaleLowerCase())) {
        return -1;
      }
    });

    this.filteredGroups$ = of(this.groups);

    this.filteredGroups$ = this.form.controls['parserId'].valueChanges.pipe(
      startWith(''),
      map((filterString) => this.filter(filterString)),
    );
  }

  private filterChildren(children: Group['children'], filterValue: string) {
    return children.filter((optionValue) => optionValue.display.toLowerCase().includes(filterValue));
  }

  private filter(value: string): Group[] {
    const filterValue = value.toLowerCase();
    return this.groups
      .map((group) => {
        return {
          name: group.name,
          countryCode: group.countryCode,
          children: this.filterChildren(group.children, filterValue),
        };
      })
      .filter((group) => group.children.length);
  }

  trackByFn(_index, item) {
    return item.name;
  }

  initForm() {
    this.form = this.fb.group({
      parserId: ['', this.parserIdValidator.bind(this)],
      files: ['', this.fileValidator.bind(this)],
    });
  }

  fileValidator() {
    if (this.fileCheckError) return { fileCheckError: this.fileCheckError };
    if (!this.fileData?.length) return { required: true };
    if (this.pageCountTotal > this.totalCreditCount) return { insufficientCredits: true };
    return null;
  }

  parserIdValidator() {
    if (!this.form || !this.docparserResults) {
      return { required: false };
    }

    const value = this.form.controls['parserId'].value;
    const docparserResultIds = this.docparserResults.map((a) => a.dpParserId);
    if (!docparserResultIds.includes(value)) {
      return { required: false };
    } else {
      return null;
    }
  }

  getFileNames() {
    return this.fileInputEl ? (Array.from(this.fileInputEl.nativeElement.files) as File[]).map((a) => a.name).join(', ') : [''];
  }

  async getDocparserDocs() {
    this.fetchingBanks = true;
    try {
      const parserDoc = (await firestore().doc(`parsers/all`).get()).data() as ParserDoc;
      this.docparserResults = environment.production ? parserDoc.parsers.filter((a) => !a.isTest) : parserDoc.parsers;

      if (this.isAiParserEnabled) {
        this.docparserResults.push({
          country: 'All',
          countryCode: CountryCode.Other,
          dpLabel: 'Automatic',
          dpParserId: 'aiparser',
          isTest: false,
          name: 'Automatic',
        });
      }
    } catch (error) {
      console.error(error);
      alert(error);
    }
    this.fetchingBanks = false;
  }

  async fileInputChange(_event: Event) {
    this.fileData = null;
    this.fileCheckError = null;
    this.processing = true;
    try {
      const fileData = await this.fileHandler.checkForErrors(this.fileInputEl.nativeElement.files);
      if (fileData?.some((a) => a.pageCount >= 80)) {
        throw Error('The maximum allowed page count is 80');
      }
      this.fileData = fileData;
    } catch (error) {
      this.fileCheckError = error.message;
      this.form.controls.files.setValue(null); // Allows re-selecting the same file
      this.toastr.warning(error.message, 'Invalid File');
    } finally {
      this.processing = false;
    }
    this.pageCountTotal = this.fileData?.reduce((total, a) => total + a.pageCount, 0);
    this.form.controls.files.updateValueAndValidity();
  }

  async parserIdChange(event: Event) {
    if (!this.docparserResults) {
      return;
    }
    const parserId = this.form.controls.parserId.value;
    const parser = this.docparserResults.find((a) => a.dpParserId === parserId);
    this.countryMismatchError = null;
    if (!parser) {
      return;
    }

    if (parser.countryCode !== this.user.countryCode) {
      this.countryMismatchError = `You have selected a bank from ${parser.country}. Was this intentional?`;
    }
  }

  async nextClicked(_event: Event) {
    if (this.form.invalid) {
      return;
    }

    this.conversionData.files = this.fileData;

    const selectedDocparserResult = this.docparserResults.find((a) => a.dpParserId === this.form.controls.parserId.value);

    this.conversionData.parserId = selectedDocparserResult.dpParserId;
    this.conversionData.parserDisplay = selectedDocparserResult.name;
    this.stepperControl.setErrors(null);
    this.stepper.next();
  }

  addCreditsClicked() {
    this.dialogService.open(AddCreditsComponent);
  }

  async noBankOptionClicked() {
    const result = await this.dialogService
      .open(DialogComponent, {
        context: {
          title: `Can't find your bank?`,
          body: `<div class="text-center"> <i class="small">We are constantly updating and improving our
          product.</i></div> <br>
          To add support for your bank’s statements please send an email to <b>admin@swiftcsv.com</b>
          with an original PDF bank statement of your bank attached.`,
          cancel: 'Close',
          confirm: '',
        },
      })
      .onClose.pipe(take(1))
      .toPromise();
  }

  onUseNewParserChange(checked: boolean) {
    if (checked) {
      this.form.controls.parserId.setValue('aiparser');
      this.form.controls.files.updateValueAndValidity();
    } else {
      this.form.controls.parserId.setValue('');
      this.form.controls.files.updateValueAndValidity();
    }
  }

  viewHandle = (value: string) => {
    const parser = this.docparserResults.find((a) => a.dpParserId === value);
    if (!parser) {
      return value;
    }
    return `${parser.countryCode ?? 'Unknown'} | ${parser.name}`;
  };
}
